import { Card } from "@/components/ui/card";
import { Flame, TrendingUp, TrendingDown, Zap, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";

interface AdvancedStatsComboProps {
  matchHistory: Array<{ 
    userWon: boolean;
    userTeam: number;
    score?: any;
  }>;
}

export function AdvancedStatsCombo({ matchHistory }: AdvancedStatsComboProps) {
  let currentStreak = 0;
  let bestWinStreak = 0;
  let tempWinStreak = 0;

  // Calculate current streak (wins or losses from most recent)
  if (matchHistory.length > 0) {
    const firstResult = matchHistory[0].userWon;
    currentStreak = 1;
    for (let i = 1; i < matchHistory.length; i++) {
      if (matchHistory[i].userWon === firstResult) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  // Calculate best WIN streak only (not losses)
  for (let i = 0; i < matchHistory.length; i++) {
    if (matchHistory[i].userWon) {
      tempWinStreak++;
      if (tempWinStreak > bestWinStreak) {
        bestWinStreak = tempWinStreak;
      }
    } else {
      tempWinStreak = 0;
    }
  }

  const currentStreakWins = matchHistory.length > 0 ? matchHistory[0].userWon : true;

  const setWins = [0, 0, 0];
  const setPlayed = [0, 0, 0];

  matchHistory.forEach(match => {
    if (match.score && match.score.sets && Array.isArray(match.score.sets)) {
      match.score.sets.forEach((set: any, index: number) => {
        if (index < 3) {
          setPlayed[index]++;
          const team1 = set.team1 || 0;
          const team2 = set.team2 || 0;
          
          // Determine if user won the set based on their team assignment
          const userTeam = match.userTeam || 1; // Default to Team 1 for backward compatibility
          if (userTeam === 1) {
            if (team1 > team2) setWins[index]++;
          } else {
            if (team2 > team1) setWins[index]++;
          }
        }
      });
    }
  });

  const setRates = setWins.map((wins, idx) => 
    setPlayed[idx] > 0 ? Math.round((wins / setPlayed[idx]) * 100) : 0
  );

  const getStreakLevel = (streak: number) => {
    if (streak >= 10) return { label: "Legendario", color: "text-yellow-500" };
    if (streak >= 7) return { label: "Épico", color: "text-purple-500" };
    if (streak >= 5) return { label: "Fuego", color: "text-orange-500" };
    if (streak >= 3) return { label: "Caliente", color: "text-red-500" };
    return null;
  };

  const currentLevel = getStreakLevel(currentStreak);

  return (
    <div className="space-y-4" data-testid="card-advanced-stats-combo">
      <Card className="p-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Momentum</span>
        </div>
        
        <div className="flex gap-3">
          <div className={cn(
            "flex-1 rounded-lg p-3 relative overflow-hidden transition-all duration-300",
            currentStreakWins 
              ? "bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20" 
              : "bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20"
          )}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Racha Actual</p>
                <div className="flex items-baseline gap-1.5">
                  <span className={cn(
                    "text-3xl font-black tabular-nums",
                    currentStreakWins ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  )}>
                    {currentStreak}
                  </span>
                  {currentStreak > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {currentStreakWins ? "victorias" : "derrotas"}
                    </span>
                  )}
                </div>
                {currentLevel && (
                  <span className={cn("text-xs font-bold", currentLevel.color)}>
                    {currentLevel.label}
                  </span>
                )}
              </div>
              <div className={cn(
                "p-2 rounded-full",
                currentStreakWins 
                  ? "bg-green-500/20 text-green-600 dark:text-green-400" 
                  : "bg-red-500/20 text-red-600 dark:text-red-400"
              )}>
                {currentStreakWins ? (
                  <TrendingUp className="h-5 w-5 animate-pulse" />
                ) : (
                  <TrendingDown className="h-5 w-5" />
                )}
              </div>
            </div>
            {currentStreak >= 3 && currentStreakWins && (
              <div className="absolute -top-1 -right-1">
                <Flame className="h-6 w-6 text-orange-500 animate-pulse drop-shadow-lg" />
              </div>
            )}
          </div>

          <div className="flex-1 rounded-lg p-3 bg-gradient-to-br from-amber-500/10 to-orange-600/5 border border-amber-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Mejor Racha</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-black tabular-nums text-amber-600 dark:text-amber-400">
                    {bestWinStreak}
                  </span>
                  {bestWinStreak > 0 && (
                    <span className="text-xs text-muted-foreground">
                      V
                    </span>
                  )}
                </div>
              </div>
              <div className="p-2 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400">
                <Trophy className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Win Rate por Set</span>
        </div>
        
        <div className="flex items-end justify-around gap-4">
          {[1, 2, 3].map((setNum) => {
            const rate = setRates[setNum - 1];
            const played = setPlayed[setNum - 1];
            const barHeight = played > 0 ? Math.max(rate, 10) : 10;
            
            return (
              <div key={setNum} className="flex flex-col items-center gap-2 flex-1">
                <div className="relative w-full flex justify-center">
                  <div 
                    className={cn(
                      "w-10 rounded-t-md transition-all duration-500 relative overflow-hidden",
                      played > 0 
                        ? rate >= 60 
                          ? "bg-gradient-to-t from-green-600 to-green-400" 
                          : rate >= 40 
                            ? "bg-gradient-to-t from-amber-600 to-amber-400"
                            : "bg-gradient-to-t from-red-600 to-red-400"
                        : "bg-muted"
                    )}
                    style={{ height: `${barHeight * 0.7}px` }}
                  >
                    {played > 0 && rate >= 60 && (
                      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-white/30 animate-pulse" />
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <p className={cn(
                    "text-lg font-bold tabular-nums",
                    played > 0 
                      ? rate >= 60 
                        ? "text-green-600 dark:text-green-400" 
                        : rate >= 40 
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-red-600 dark:text-red-400"
                      : "text-muted-foreground"
                  )}>
                    {rate}%
                  </p>
                  <p className="text-xs text-muted-foreground font-medium">Set {setNum}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
